// Identification document types supported for KYC verification
export const DOCUMENT_TYPES = Object.freeze({
  NATIONAL_ID: "nat_identity",
  DRIVER_PERMIT: "driver",
  PASSPORT: "passport",
  BIRTH_CERTIFICATE: "birth_cert",
});

// Maximum allowed upload size (10 megabytes)
export const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

// Trinidad & Tobago branch options
export const BRANCH_CHOICES = Object.freeze([
  "Arima",
  "Barataria",
  "Chaguanas",
  "Port of Spain",
  "Sande Grande",
  "San Fernando",
  "St. Joseph - La Joya",
  "Tobago",
]);

// Honorific title options
export const TITLE_CHOICES = Object.freeze(["Mr.", "Mrs.", "Miss."]);

// Nominee / Dependent relationship options
export const RELATIONSHIP_CHOICES = Object.freeze([
  "Father",
  "Mother",
  "Friends",
  "Other family member",
]);

// Average income bracket options
export const INCOME_RANGES = Object.freeze([
  "under $1500",
  "$1,501-$3,000",
  "$3,001-$4,500",
  "$4,501-$6,000",
  "$6,001-$7500",
  "$7,501-$9,000",
  "$9,001-$11,000",
  "over $11,000",
]);

// Source of income / Employment type options
export const INCOME_SOURCE_CHOICES = Object.freeze([
  "Salaried",
  "Business",
  "Unemployed",
  "Retired",
  "Self-employed",
]);

// Account funding mechanism options
export const FUNDING_SOURCE_CHOICES = Object.freeze([
  "Salary Deduction",
  "Standing Order from Financial Institution",
  "Over the Counter Deposits",
  "Other",
]);

// Months of the year for date and duration selection
export const MONTH_CHOICES = Object.freeze([
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]);

// Year options for duration selection (1950 to 2059)
export const YEAR_CHOICES = Object.freeze(
  Array.from({ length: 2059 - 1950 + 1 }, (_, index) => String(1950 + index)),
);

// Standard list of recognized sovereign nations (195 countries)
export const COUNTRY_CHOICES = Object.freeze([
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Côte d voire",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo Congo-Brazzaville)",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czechia (Czech Republic)",
  "Democratic Republic of the Congo",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Holy See",
  "Honduras",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar (formerly Burma)",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia (formerly Macedonia)",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine State",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome nd Princip",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon slands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States of America",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
]);

// International dialing country codes (221 phone codes)
export const PHONE_COUNTRY_CODE_CHOICES = Object.freeze([
  "+93-AFG",
  "+355-ALB",
  "+213-DZA",
  "+376-AND",
  "+244-AGO",
  "+1-ATG",
  "+54-ARG",
  "+374-ARM",
  "+297-AABW",
  "+61-AUS",
  "+43-AUT",
  "+994-AZE",
  "+1-BHS",
  "+973-BHR",
  "+880-BGD",
  "+1-BRB",
  "+375-BLR",
  "+32-BEL",
  "+501-BLZ",
  "+229-BEN",
  "975-BTN",
  "+591-BOL",
  "+387-BIH",
  "+267-BWA",
  "+55-BRA",
  "+673-BRN",
  "+359-BGR",
  "+226-BFA",
  "+257-BDI",
  "+855-KHM",
  "+237-CMR",
  "+1-CAN",
  "+238-CPV",
  "+236-CAF",
  "+235-TCD",
  "+56-CHL",
  "+86-CHN",
  "+57-COL",
  "+269-COM",
  "+242-COG",
  "+682-COK",
  "506-CRI",
  "+225-CIV",
  "+385-HRV",
  "+53-CUB",
  "+357-CYP",
  "+420-CZE",
  "+45-DNK",
  "+253-DJI",
  "+1-DMA",
  "+1-DOM",
  "+593-ECU",
  "20-EGY",
  "+503-SLV",
  "+240-GNQ",
  "+291-ERI",
  "+372-EST",
  "+251-ETH",
  "+500-FLK",
  "+298-FRO",
  "+679-FJI",
  "+358-FIN",
  "+33-FRA",
  "+689-PYF",
  "+241-GAB",
  "+220-GMB",
  "+995-GEO",
  "+49-DEU",
  "+233-GHA",
  "+350-GIB",
  "+30-GRC",
  "+299-GRL",
  "+1-GRD",
  "+502-GTM",
  "224-GIN",
  "+245-GNB",
  "+592-GUY",
  "+509-HTI",
  "+504-HND",
  "+852-HKG",
  "+36-HUN",
  "+354-ISL",
  "+91-IND",
  "+62-IDN",
  "+98-IRN",
  "964-IRQ",
  "+353-IRL",
  "+44-IMN",
  "+972-ISR",
  "+39-ITA",
  "+1-JAM",
  "+81-JPN",
  "+962-JOR",
  "+7-KAZ",
  "+254-KEN",
  "+686-KIR",
  "383-XKX",
  "+850-PRK",
  "+82-KOR",
  "+965-KWT",
  "+996-KGZ",
  "+856-LAO",
  "+371-LVA",
  "+961-LBN",
  "+266-LSO",
  "+231-LBR",
  "+218-LBY",
  "+423-LIE",
  "+370-LTU",
  "+352-LUX",
  "+853-MAC",
  "+389-MKD",
  "+261-MDG",
  "+265-MWI",
  "+60-MYS",
  "+960-MDV",
  "+223-MLI",
  "+356-MLT",
  "+692-MHL",
  "+222-MRT",
  "+230-MUS",
  "+262-REU",
  "+52-MEX",
  "+691-FSM",
  "+373-MDA",
  "+377-MCO",
  "+976-MNG",
  "+382-MNE",
  "+1-MSR",
  "+212-MAR",
  "+258-MOZ",
  "+95-MMR",
  "+264-NAM",
  "+674-NRU",
  "+977-NPL",
  "+31-NLD",
  "+599-NLD",
  "687-NCL",
  "+64-NZL",
  "+505-NIC",
  "+227-NER",
  "+234-NGA",
  "+683-NIU",
  "+672-NFK",
  "+47-NOR",
  "+968-OMN",
  "+92-PAK",
  "+680-PLW",
  "970-PSE",
  "+507-PAN",
  "+675-PNG",
  "+595-PRY",
  "+51-PER",
  "+63-PHL",
  "+48-POL",
  "+351-PRT",
  "+1-PRK",
  "+974-QAT",
  "+40-ROU",
  "7-RUS",
  "+250-RWA",
  "+590-BLM",
  "+290-SHN",
  "+1-KNA",
  "+1-LCA",
  "+590-MFA",
  "+508-SPM",
  "+1-VCT",
  "+685-WSM",
  "+378-SMR",
  "239-STP",
  "+966-SAU",
  "+221-SEN",
  "+381-SRB",
  "+248-SYC",
  "+232-SLE",
  "+65-SGP",
  "+599-SXM",
  "+421-SVK",
  "+386-SVN",
  "+677-SLB",
  "+252-SOM",
  "+27-ZAF",
  "+211-SSD",
  "+34-ESP",
  "+94-LKA",
  "+249-SDN",
  "+597-SUR",
  "+268-SWZ",
  "+46-SWE",
  "+41-CHE",
  "963-SYR",
  "+886-TWN",
  "+992-TJK",
  "+255-TZA",
  "+66-THA",
  "+670-TLS",
  "+228-TGO",
  "+690-TKL",
  "+676-TON",
  "+868-TTO",
  "+216-TUN",
  "90-TUR",
  "+993-TKM",
  "+688-TUV",
  "+256-UGA",
  "+380-UKR",
  "+971-ARE",
  "+44-GBR",
  "+1-USA",
  "+598-URY",
  "+998-UZB",
  "+678-VUT",
  "39-VA",
  "+58-VEN",
  "+84-VNM",
  "+681-WLF",
  "+212-ESH",
  "+967-YEM",
  "+260-ZMB",
  "+263-ZWE",
]);

// Document issuance jurisdiction country list (244 countries & territories)
export const DOCUMENT_ISSUANCE_COUNTRIES = Object.freeze([
  "Afghanistan",
  "Aland Islands",
  "Albania",
  "Algeria",
  "American Samoa",
  "Andorra",
  "Angola",
  "Anguilla",
  "Antarctica",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Aruba",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bermuda",
  "Bhutan",
  "Bolivia",
  "Bonaire",
  "Bosnia and Herzegovina",
  "Botswana",
  "Bouvet Island",
  "Brazil",
  "British Indian Ocean Territory",
  "British Virgin Islands",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Cape Verde",
  "Cayman Islands",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Christmas Island",
  "Cocos (Keeling) Islands",
  "Colombia",
  "Comoros",
  "Congo (Brazzaville)",
  "Congo (Kinshasa)",
  " Cook Islands",
  "Costa Rica",
  "Croatia",
  "Curaçao",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "East Timor",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Ethiopia",
  "Falkland Islands",
  "Faroe Islands",
  "Fiji",
  "Finland",
  "France",
  "French Guiana",
  "French Polynesia",
  "French Southern Territories",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Gibraltar",
  "Greece",
  "Greenland",
  "Grenada",
  "Guadeloupe",
  "Guam",
  "Guatemala",
  "Guernsey",
  "Guinea",
  "Guinea-Bissau",
  "uyana",
  "Haiti",
  "Heard Island and McDonald Islands",
  "Honduras",
  "Hong Kong",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Ireland",
  "Isle of Man",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jersey",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Macau",
  "Macedonia",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Martinique",
  "Mauritania",
  "Mauritius",
  "Mayotte",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Montserrat",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Caledonia",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "Niue",
  "Norfolk Island",
  "Northern Mariana Islands",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestinian Territories",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Pitcairn",
  "Poland",
  "Portugal",
  "Puerto Rico",
  "Qatar",
  "Republic of Côte d’Ivoire",
  "Republic of Kosovo",
  "Reunion",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Barthélemy",
  "Saint Helena",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Martin",
  "Saint Pierre and Miquelon",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Sint Maarten",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Georgia and the South Sandwich Islands",
  "South Korea",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Svalbard and Jan Mayen",
  "Swaziland",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Togo",
  "Tokelau",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Turks and Caicos Islands",
  "Tuvalu",
  "U.S. Virgin Islands",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "United States Minor Outlying Islands",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican",
  "Venezuela",
  "Vietnam",
  "Wallis and Futuna",
  "Western Sahara",
  "Yemen",
  "Zambia",
  "Zimbabwe",
]);

// Backward-compatible named aliases for form dropdown bindings
export const SELECTED_MONTH_OPTIONS = MONTH_CHOICES;
export const SELECTED_YEAR_OPTIONS = YEAR_CHOICES;
export const TITLE_OPTIONS = TITLE_CHOICES;
export const RELATION_OPTIONS = RELATIONSHIP_CHOICES;
export const BRANCH_OPTIONS = BRANCH_CHOICES;
export const INCOME_OPTIONS = INCOME_SOURCE_CHOICES;
export const FUNDED_OPTIONS = FUNDING_SOURCE_CHOICES;
export const AVERAGE_INCOME_OPTIONS = INCOME_RANGES;
export const COUNTRY_OPTIONS = COUNTRY_CHOICES;
export const PHONE_CODE_OPTIONS = PHONE_COUNTRY_CODE_CHOICES;
export const ISSUANCE_COUNTRY_OPTIONS = DOCUMENT_ISSUANCE_COUNTRIES;
